// The company as its documents show it (ERP-71): the logo and the letterhead an invoice prints.
import { getBlob, useApi, useAuth } from "@keel/core";
import { useEffect, useState } from "react";

/** What a member may see of a company: what it prints on its documents anyway. */
export interface CompanyProfile {
  id: string;
  code: string;
  name: string;
  legalName: string | null;
  registrationNumber: string | null;
  vatNumber: string | null;
  vatRegistered: boolean;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  country: string;
  currency: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  invoiceFooter: string | null;
  representativeName?: string | null;
  representativeTitle?: string | null;
  parentCompanyId: string | null;
  logoUpdatedAt: string | null;
}

/**
 * An object URL for the company's logo, or null while there is none. The image needs the
 * session's Authorization header, so it is fetched rather than linked.
 */
export function useCompanyLogo(companyId: string | undefined, updatedAt: string | null | undefined) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!companyId || !updatedAt) {
      setUrl(null);
      return;
    }
    let made: string | null = null;
    let live = true;
    getBlob(`/companies/${companyId}/logo`)
      .then((b) => {
        made = URL.createObjectURL(b);
        if (live) setUrl(made);
      })
      .catch(() => live && setUrl(null));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [companyId, updatedAt]);
  return url;
}

/** The current company's profile, or the given one's. */
export function useCompanyProfile(companyId?: string) {
  const { company } = useAuth();
  const id = companyId ?? company?.id;
  return useApi<CompanyProfile>(id ? `/companies/${id}` : null).data;
}

/** One line of the address: "Brīvības iela 1, Rīga, LV-1010, LV". */
export const addressLine = (c: Pick<CompanyProfile, "address" | "city" | "postalCode" | "country">) =>
  [c.address, c.city, c.postalCode, c.country].filter(Boolean).join(", ");

/**
 * The issuer's block at the top of a printed document: logo, legal name, numbers, address and
 * contacts. Printed only, so the screen keeps its own layout, unless it is a `preview`.
 */
export function Letterhead({
  companyId,
  title,
  preview,
}: {
  companyId?: string;
  title?: string;
  /** Show it on screen too: the company page's preview. */
  preview?: boolean;
}) {
  const c = useCompanyProfile(companyId);
  const logo = useCompanyLogo(c?.id, c?.logoUpdatedAt);
  if (!c) return null;
  return (
    <header className={`letterhead ${preview ? "preview" : "print-only"}`}>
      <div className="letterhead-who">
        {logo && <img src={logo} alt="" className="letterhead-logo" />}
        <div>
          <div className="letterhead-name">{c.legalName || c.name}</div>
          <div className="letterhead-meta">
            {c.registrationNumber && <span>Reg. No. {c.registrationNumber}</span>}
            {c.vatRegistered && c.vatNumber && <span>VAT {c.vatNumber}</span>}
          </div>
          <div className="letterhead-meta">{addressLine(c)}</div>
          <div className="letterhead-meta">
            {[c.phone, c.email, c.website].filter(Boolean).map((x) => (
              <span key={x}>{x}</span>
            ))}
          </div>
        </div>
      </div>
      {title && <div className="letterhead-title">{title}</div>}
    </header>
  );
}

/** The bottom of a printed document: where to pay, and the company's own footer text. */
export function DocumentFooter({
  companyId,
  bank,
}: {
  companyId?: string;
  bank?: { name: string; iban: string | null; bic: string | null } | null;
}) {
  const c = useCompanyProfile(companyId);
  if (!c) return null;
  return (
    <footer className="doc-footer print-only">
      {bank?.iban && (
        <div>
          <b>Payment to</b> {c.legalName || c.name} · {bank.name} · IBAN {bank.iban}
          {bank.bic ? ` · BIC ${bank.bic}` : ""}
        </div>
      )}
      {c.invoiceFooter && <div className="doc-footer-text">{c.invoiceFooter}</div>}
    </footer>
  );
}
